import { useEffect, useState } from "react";
import {
  Alert,
  Avatar,
  Badge,
  Button,
  Card,
  CardBody,
  EmptyState,
  Progress,
  Skeleton,
  SkeletonText,
  Spinner,
} from "@one-nexty/components-web";

import { Preview } from "../ui/preview";
import { Section, SubSection } from "../ui/section";
import { InboxIcon, PlusIcon } from "../ui/icons";

export function FeedbackSection() {
  // Progression animée en boucle, pour montrer la transition de largeur.
  const [progress, setProgress] = useState(35);
  useEffect(() => {
    const timer = setInterval(() => setProgress((value) => (value >= 100 ? 15 : value + 5)), 900);
    return () => clearInterval(timer);
  }, []);

  const [dismissed, setDismissed] = useState(false);

  return (
    <Section
      id="feedback"
      eyebrow="Feedback"
      title="Alertes & états de chargement"
      description="Un rail de trois pixels sur le bord gauche plutôt qu'un aplat teinté : trois alertes empilées restent lisibles, et le texte garde un contraste maximal."
    >
      <SubSection title="Alertes" hint="variant=rail — le défaut">
        <Preview
          layout="stack"
          label="alerts.tsx"
          code={`<Alert tone="info" title="Nouvelle version disponible">
  La version 0.4.0 ajoute huit types de champs.
</Alert>

<Alert
  tone="danger"
  title="Le build a échoué"
  actions={<Button size="sm" variant="danger">Relancer</Button>}
>
  Erreur de typage dans packages/tokens/build.mjs.
</Alert>`}
        >
          <Alert tone="info" title="Nouvelle version disponible">
            La version 0.4.0 ajoute huit types de champs et refond les états de chargement.
          </Alert>
          <Alert tone="success" title="Déploiement réussi">
            Le site est en ligne depuis 2 minutes.
          </Alert>
          <Alert tone="warning" title="Quota bientôt atteint">
            Il reste 12 % de votre quota mensuel.
          </Alert>
          <Alert
            tone="danger"
            title="Le build a échoué"
            actions={
              <>
                <Button size="sm" variant="outline">
                  Voir les logs
                </Button>
                <Button size="sm" variant="danger">
                  Relancer
                </Button>
              </>
            }
          >
            Erreur de typage dans <code className="font-mono">packages/tokens/build.mjs</code>.
          </Alert>
        </Preview>
      </SubSection>

      <SubSection title="Alertes — variantes" hint="tinted pour ce qui doit dominer la page">
        <Preview
          layout="stack"
          label="variants.tsx"
          code={`<Alert tone="danger" variant="tinted" title="Paiement refusé">…</Alert>
<Alert tone="neutral" hideIcon>…</Alert>
<Alert tone="info" title="Fermable" onDismiss={() => setDismissed(true)}>…</Alert>`}
        >
          <Alert tone="danger" variant="tinted" title="Paiement refusé">
            Mettez à jour votre moyen de paiement avant le 30 du mois.
          </Alert>
          <Alert tone="neutral" hideIcon>
            Une alerte neutre sans icône, pour un simple encadré d'information.
          </Alert>
          {!dismissed && (
            <Alert tone="info" title="Alerte fermable" onDismiss={() => setDismissed(true)}>
              La croix joue une sortie animée avant le retrait.
            </Alert>
          )}
          {dismissed && (
            <Button variant="outline" size="sm" onClick={() => setDismissed(false)}>
              Rétablir l'alerte
            </Button>
          )}
        </Preview>
      </SubSection>

      <SubSection
        title="Progression linéaire"
        hint="Révélée par clip-path : aucune largeur n'est animée"
      >
        <Preview
          layout="stack"
          className="max-w-md"
          label="progress.tsx"
          code={`<Progress label="Téléversement" value={progress} showValue />
<Progress label="Stockage utilisé" value={82} showValue tone="warning" />

{/* Sans value : mode indéterminé */}
<Progress label="Analyse en cours" />`}
        >
          <Progress label="Téléversement" value={progress} showValue />
          <Progress label="Stockage utilisé" value={82} showValue tone="warning" />
          <Progress label="Quota dépassé" value={100} showValue tone="danger" size="sm" />
          <Progress label="Analyse en cours (indéterminée)" />
        </Preview>
      </SubSection>

      <SubSection
        title="Progression en anneau"
        hint="Même information dans un carré compact"
      >
        <Preview
          label="ring.tsx"
          code={`<Progress variant="ring" value={72} showValue label="Couverture" tone="success" />
<Progress variant="ring" size="sm" label="Indéterminée" />`}
        >
          <Progress variant="ring" value={progress} showValue label="Téléversement" />
          <Progress variant="ring" value={72} showValue label="Couverture" tone="success" />
          <Progress variant="ring" value={94} showValue label="Stockage" tone="danger" size="lg" />
          <Progress variant="ring" size="sm" label="Indéterminée" />
        </Preview>
      </SubSection>

      <SubSection
        title="Attente indéterminée"
        hint="Dégradé conique + masque radial — un seul élément, aucun tracé SVG"
      >
        <Preview
          label="spinner.tsx"
          code={`<Spinner size="lg" className="text-accent" />

<span className="inline-flex items-center gap-2">
  <Spinner size="sm" label={null} /> Chargement…
</span>

<Button loading>Enregistrement</Button>`}
        >
          <Spinner size="xs" />
          <Spinner size="sm" />
          <Spinner size="md" />
          <Spinner size="lg" className="text-accent" />
          <span className="inline-flex items-center gap-2 text-sm text-fg-muted">
            <Spinner size="sm" label={null} /> Chargement des données…
          </span>
          <Button loading>Enregistrement</Button>
        </Preview>
      </SubSection>

      <SubSection title="Squelettes" hint="Balayage translaté sur le GPU, jamais un background-position">
        <div className="grid gap-5 md:grid-cols-2">
          <Card>
            <CardBody className="flex gap-4">
              <Skeleton shape="circle" className="size-10 shrink-0" />
              <SkeletonText lines={3} />
            </CardBody>
          </Card>

          {/* Le même bloc une fois chargé : les gabarits doivent coïncider. */}
          <Card>
            <CardBody className="flex gap-4">
              <Avatar name="Camille Roy" size="md" className="shrink-0" />
              <div className="flex flex-col gap-1">
                <p className="text-sm font-medium text-fg">Camille Roy</p>
                <p className="text-sm text-fg-muted">
                  A publié trois nouveaux composants dans la bibliothèque partagée.
                </p>
                <Badge tone="brand" className="mt-1 w-fit">
                  Design
                </Badge>
              </div>
            </CardBody>
          </Card>
        </div>
      </SubSection>

      <SubSection title="État vide" hint="Toujours accompagné de l'action qui permet d'en sortir">
        <EmptyState
          icon={<InboxIcon />}
          title="Aucun projet pour le moment"
          description="Créez votre premier projet pour commencer à documenter vos composants."
          action={<Button leadingIcon={<PlusIcon />}>Créer un projet</Button>}
        />
      </SubSection>
    </Section>
  );
}
